<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import type { DataChartLegendKpiItem } from "@modules/data/components/data/chart/dataChartLegendKpi.types"

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    /**
     * Titre optionnel au-dessus de la liste (ex. titre du graphique).
     */
    title?: string
    /**
     * Lignes de légende : une entrée par tracé, avec valeur déjà formatée.
     */
    items: readonly DataChartLegendKpiItem[]
    /**
     * Niveau du titre, pour que la hiérarchie des titres de la PAGE d'accueil
     * reste continue (règle `heading-order` d'axe-core). Ajout additif : le
     * défaut `h4` est le niveau rendu jusqu'ici, aucun appelant existant ne
     * change. Une carte dont le titre est un `h2` pose `h3`, sans quoi le
     * lecteur d'écran saute un niveau.
     */
    titleTag?: "h2" | "h3" | "h4" | "h5"
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    title: "",
    items: () => [],
    titleTag: "h4",
})

// ================================
// COMPUTED
// ================================
/** Indique s’il existe au moins une ligne à afficher. */
const getHasItems = computed(() => props.items.length > 0)
</script>

<template>
    <!-- ================================ -->
    <!-- LÉGENDE KPI (tracés + totaux) -->
    <!-- ================================ -->
    <div
        v-if="getHasItems"
        class="data-chart-legend-kpi flex w-full flex-col gap-3"
        data-component="DataChartLegendKPI"
    >
        <component
            :is="props.titleTag"
            v-if="title"
            class="text-xs font-medium uppercase tracking-wide text-primary-600"
        >
            {{ title }}
        </component>
        <div
            class="flex w-full flex-row flex-wrap justify-between gap-x-8 gap-y-2 text-sm text-surface-700"
        >
            <div
                v-for="(item, index) in items"
                :key="`${item.name}-${index}`"
                class="data-chart-legend-kpi__row flex min-w-[10rem] max-w-full flex-row items-stretch gap-2"
            >
                <div
                    class="w-1 shrink-0 self-stretch rounded-sm"
                    :style="{ backgroundColor: item.color }"
                    aria-hidden="true"
                />
                <div class="flex min-w-0 flex-col gap-0.5 py-0.5">
                    <span class="text-xs font-medium text-surface-800">{{
                        item.name
                    }}</span>
                    <span
                        class="text-sm font-medium tabular-nums text-surface-900"
                    >
                        {{ item.value
                        }}<template v-if="item.unit"
                            >&nbsp;{{ item.unit }}</template
                        >
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>
